/**
 * SMSチャットアプリ用ストア
 *
 * @author Tsue Shogo
 * @since 2023-04-20
 */
import { SMSAxios } from '@/utils/smsAxios'
import { RepositoryFactory } from '@/api/RepositoryFactory'

/**
 * localStorageに保存されたjsonデータを取得する
 *
 * @param {string}  key
 * @param {any} defaultValue
 * @returns {any}
 */
export const getJsonStorage = (key, defaultValue) => {
  try {
    const json = localStorage.getItem(key)
    const data = (json && JSON.parse(json)) || defaultValue
    return data
  } catch (_err) {
    return defaultValue
  }
}

/**
 * state
 */
const state = {
  /**
   * アクセストークン
   *
   * @type {string|null}
   */
  accessToken: null,

  /**
   * リフレッシュトークン
   *
   * @type {string|null}
   */
  refreshToken: null,

  /**
   * ログインリトライフラグ
   *
   * @type {boolean}
   */
  loginRetry: false,

  /**
   * APIbaseURL
   *
   * @type {string|null}
   */
  baseURL: null
}

/**
 * getters
 */
const getters = {
  /**
   * stateから単純取得
   *
   * @param {T} state
   * @param {keyof T} stateKey
   * @return {any}
   */
  get: (state) => (stateKey) => {
    return state[stateKey]
  }
}

/**
 * mutations
 */
const mutations = {
  /**
   * stateに単純代入
   *
   * @param {T} state
   * @param {
   *  stateKey: keyof T;
   *  data: any;
   * }
   * @return {void}
   */
  set (state, { stateKey, data }) {
    console.log('[SMSChateMessage][set] ', stateKey, data)
    state[stateKey] = data
  },

  /**
   * stateにストレージ代入
   *
   * @param {T} state
   * @param {
   *  stateKey: keyof T;
   *  data: any;
   * }
   * @return {void}
   */
  setStorage (state, { stateKey, data }) {
    console.log('[SMSChateMessage][setStorage] ', stateKey, data)
    if (typeof (localStorage) !== 'undefined') {
      state[stateKey] = data
      const storageKey = stateKey
      localStorage.setItem(storageKey, JSON.stringify(data))
    } else {
      state[stateKey] = data
    }
  }
}

/**
 * actions
 */
const actions = {
  /**
   * アクセストークンとリフレッシュトークンの保存
   *
   * @param {
   *  accessToken: string;
   *  refreshToken: string;
   * }
   * @returns {Promise<void>}
   */
  async saveTokens({ commit }, { accessToken, refreshToken }) {
    // ストアに保存
    commit('set', { stateKey: 'accessToken', data: accessToken })
    commit('set', { stateKey: 'refreshToken', data: refreshToken })

    // CRMDBに保存
    const UserRepository = RepositoryFactory.get('users')
    await UserRepository.updateUserAdditionalInfo({
      additional_info: {
        sms_access_token: accessToken,
        sms_refresh_token: refreshToken
      }
    })
  },

  /**
   * インスタンスを準備する
   *
   * @param {
   *  forceFlg?: boolean;
   * }
   * @returns {AxiosInstance|null}
   */
  readyInstance({ getters }, param) {
    const baseURL = getters.get('baseURL')
    if (!baseURL) {
      return null
    }
    return SMSAxios.getInstance(baseURL, param?.forceFlg ?? false)
  },

  /**
   * ログインIDとパスワードでログインする
   *
   * @param {
   *  loginid: string;
   *  loginpw: string;
   * }
   * @returns {Promise<{
   *  access_token: string;
   *  refresh_token: string;
   * }>}
   */
  async postAdminAuthLogin({ dispatch }, { loginid, loginpw }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'POST',
      url: '/admin/auth_login/',
      data: {
        loginid,
        loginpw
      }
    })
    return result.data
  },

  /**
   * 管理者自身の情報を取得する
   *
   * @returns {Promise<{
   *  admin_no: number;
   *  admin_name: string;
   *  adm_mail: string;
   * }>}
   */
  async getAdminProfile({ dispatch }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'GET',
      url: '/admin/profile/'
    })
    return result.data
  },

  /**
   * スタッフ情報を一覧取得する
   *
   * @param {
   *  perPage?: number;
   *  page?: number;
   *  orderBy?: string;
   *  order?: 'asc'|'desc';
   *  conditions?: any;
   * }
   * @returns {Promise<{
   *  list: Staff[];
   *  total: number;
   * }>}
   */
  async getAdminStaffs({ dispatch }, {
    perPage,
    page,
    order,
    conditions
  }) {
    const axios = await dispatch('readyInstance')
    const params = {}
    if (perPage !== undefined) {
      params.per_page = perPage
    }
    if (page !== undefined) {
      params.page = page
    }
    if (order !== undefined) {
      params.order_json = JSON.stringify(order)
    }
    if (conditions !== undefined) {
      params.conditions_json = JSON.stringify(conditions)
    }
    const result = await axios({
      method: 'GET',
      url: '/admin/staffs/',
      params
    })
    return result.data
  },

  /**
   * スタッフ情報を詳細取得する
   *
   * @param {
   *  staffNo: number;
   * }
   * @returns {Promise<Staff>}
   */
  async getAdminStaff({ dispatch }, { staffNo }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'GET',
      url: `/admin/staff/${staffNo}`
    })
    return result.data
  },

  /**
   * スタッフが送受信したメッセージを一覧取得する
   *
   * @param {
   *  staffNo: number;
   *  perPage?: number;
   *  page?: number;
   *  orderBy?: string;
   *  order?: 'asc'|'desc';
   *  conditions?: any;
   * }
   * @returns {Promise<Message[]>}
   */
  async getAdminStaffMessages({ dispatch }, {
    staffNo,
    perPage,
    page,
    orderBy,
    order,
    conditions
  }) {
    const axios = await dispatch('readyInstance')
    const params = {}
    if (perPage !== undefined) {
      params.per_page = perPage
    }
    if (page !== undefined) {
      params.page = page
    }
    if (orderBy !== undefined) {
      params.order_by = orderBy
    }
    if (order !== undefined) {
      params.order = order
    }
    if (conditions !== undefined) {
      params.conditions_json = JSON.stringify(conditions)
    }
    const result = await axios({
      method: 'GET',
      url: `/admin/staff_messages/${staffNo}`,
      params
    })
    return result.data
  },

  /**
   * スタッフへメッセージを送信する
   *
   * @param {
   *  staff_no: number;
   *  from: {
   *    id: string;
   *    name: string;
   *    image?: string;
   *    color_bg?: string;
   *    color_txt?: string;
   *    text?: string;
   *  };
   *  type: 'text' | 'image' | 'pdf';
   *  text?: string;
   *  image?: string;
   *  pdf?: string;
   * }
   * @returns {Promise<Message>}
   */
  async postAdminMessage({ dispatch }, {
    staff_no,
    from,
    type,
    text,
    image,
    pdf
  }) {
    const axios = await dispatch('readyInstance')
    const data = {}
    if (staff_no !== undefined) {
      data.staff_no = staff_no
    }
    if (from !== undefined) {
      data.from = from
    }
    if (type !== undefined) {
      data.type = type
    }
    if (text !== undefined) {
      data.text = text
    }
    if (image !== undefined) {
      data.image = image
    }
    if (pdf !== undefined) {
      data.pdf = pdf
    }
    const result = await axios({
      method: 'POST',
      url: '/admin/message/',
      data
    })
    return result.data
  },

  /**
   * スタッフへメッセージを一括送信する
   *
   * @param {
   *  staff_no_list: number[];
   *  from: {
   *    id: string;
   *    name: string;
   *    image?: string;
   *    color_bg?: string;
   *    color_txt?: string;
   *    text?: string;
   *  };
   *  type: 'text' | 'image' | 'pdf';
   *  text?: string;
   *  image?: string;
   *  pdf?: string;
   * }
   * @returns {Promise<{
   *  read_at: string;
   *  from_type: string;
   *  from: {
   *    id: string;
   *    name: string;
   *    image?: string;
   *    color_bg?: string;
   *    color_txt?: string;
   *    text?: string;
   *  };
   *  type: 'text' | 'image' | 'pdf';
   *  text?: string;
   *  image?: string;
   *  pdf?: string;
   *  common_flg: boolean;
   *  staff_no_list: number[];
   * }>}
   */
  async postAdminMessages({ dispatch }, {
    staffNoList,
    from,
    type,
    text,
    image,
    pdf
  }) {
    const axios = await dispatch('readyInstance')
    const data = {}
    if (staffNoList !== undefined) {
      data.staff_no_list = staffNoList
    }
    if (from !== undefined) {
      data.from = from
    }
    if (type !== undefined) {
      data.type = type
    }
    if (text !== undefined) {
      data.text = text
    }
    if (image !== undefined) {
      data.image = image
    }
    if (pdf !== undefined) {
      data.pdf = pdf
    }
    const result = await axios({
      method: 'POST',
      url: '/admin/messages/',
      data
    })
    return result.data
  },

  /**
   * メッセージを一括で既読・未読にする
   *
   * @param {
   *  staffNo: number;
   *  readAt: string;
   *  messageIds?: number[];
   *  all?: 1;
   *  excludeIds?: number[];
   * }
   * @returns {Promise<{
   *  change_ids: number[];
   *  exclude_ids: number[];
   * }>}
   */
  async patchAdminReadMessages({ dispatch }, {
    staffNo,
    readAt,
    messageIds,
    all,
    excludeIds
  }) {
    const axios = await dispatch('readyInstance')
    const data = {}
    if (staffNo !== undefined) {
      data.staff_no = staffNo
    }
    if (readAt !== undefined) {
      data.read_at = readAt
    }
    if (messageIds !== undefined) {
      data.message_ids = messageIds
    }
    if (all !== undefined) {
      data.all = all
    }
    if (excludeIds !== undefined) {
      data.exclude_ids = excludeIds
    }
    const result = await axios({
      method: 'PATCH',
      url: '/admin/read_messages/',
      data
    })
    return result.data
  },

  /**
   * スタッフに送信したメッセージを編集する
   *
   * @param {
   *  messageId: number;
   *  readAt?: string;
   *  from?: {
   *    id: string;
   *    name: string;
   *    image?: string;
   *    color_bg?: string;
   *    color_txt?: string;
   *    text?: string;
   *  };
   *  type?: 'text' | 'image' | 'pdf';
   *  text?: string;
   *  image?: string;
   *  pdf?: string;
   * }
   * @returns {Promise<Message>}
   */
  async patchAdminMessage({ dispatch }, {
    messageId,
    readAt,
    from,
    type,
    text,
    image,
    pdf
  }) {
    const axios = await dispatch('readyInstance')
    const data = {}
    if (readAt !== undefined) {
      data.read_at = readAt
    }
    if (from !== undefined) {
      data.from = from
    }
    if (type !== undefined) {
      data.type = type
    }
    if (text !== undefined) {
      data.text = text
    }
    if (image !== undefined) {
      data.image = image
    }
    if (pdf !== undefined) {
      data.pdf = pdf
    }
    const result = await axios({
      method: 'PATCH',
      url: `/admin/message/${messageId}`,
      data
    })
    return result.data
  },

  /**
   * ファイルをストレージバケットにアップロードする
   *
   * @param {
   *  storagePath: string;
   *  file: string;
   *  override?: boolean;
   * }
   * @returns {Promise<{
   *  storage_path: string;
   *  url: string;
   * }>}
   */
  async postAdminStorage({ dispatch }, {
    storagePath,
    file,
    override
  }) {
    const axios = await dispatch('readyInstance')
    const data = {}
    if (file !== undefined) {
      data.file = file
    }
    if (override !== undefined) {
      data.override = override
    }
    const result = await axios({
      method: 'POST',
      url: `/admin/storage/${storagePath}`,
      data
    })
    return result.data
  },

  /**
   * ストレージバケットにアップロードしたファイルを削除する
   *
   * @param {
   *  storagePath: string;
   * }
   * @returns {Promise<{
   *  storage_path: string;
   *  url: string;
   * }>}
   */
  async deleteAdminStorage({ dispatch }, {
    storagePath
  }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'DELETE',
      url: `/admin/storage/${storagePath}`
    })
    return result.data
  },

  /**
   * エリア一覧を取得する
   *
   * @returns {Promise<Area>}
   */
  async getAdminAreas({ dispatch }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'GET',
      url: '/admin/areas/'
    })
    return result.data
  },

  /**
   * アプリバージョン一覧を取得する
   *
   * @returns {Promise<Area>}
   */
  async getAdminAppVersions({ dispatch }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'GET',
      url: '/admin/app_versions/'
    })
    return result.data
  },

  /**
   * グローバル設定情報を詳細取得する
   *
   * @param {string}  slug
   * @returns {Promise<GlobalSetting>}
   */
  async getAdminGlobalSetting({ dispatch }, { slug }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'GET',
      url: `/admin/global_setting/${slug}`
    }).catch((_err) => {})
    return result?.data ?? null
  },

  /**
   * グローバル設定を登録する
   *
   * @param {string}  slug
   * @param {any} content
   * @returns {Promise<GlobalSetting>}
   */
  async postAdminGlobalSetting({ dispatch }, { slug, content }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'POST',
      url: `/admin/global_setting/${slug}`,
      data: {
        content
      }
    })
    return result?.data ?? null
  },

  /**
   * グローバル設定を編集する
   *
   * @param {string}  slug
   * @param {any} content
   * @returns {Promise<GlobalSetting>}
   */
  async patchAdminGlobalSetting({ dispatch }, { slug, content }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'PATCH',
      url: `/admin/global_setting/${slug}`,
      data: {
        content
      }
    })
    return result?.data ?? null
  },

  /**
   * グローバル設定を削除する
   *
   * @param {string}  slug
   * @returns {Promise<boolean>}
   */
  async deleteAdminGlobalSetting({ dispatch }, { slug }) {
    const axios = await dispatch('readyInstance')
    const result = await axios({
      method: 'DELETE',
      url: `/admin/global_setting/${slug}`
    })
    return result?.data ?? false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
